import {useEffect} from "react";
import {useSelector} from "react-redux";

import {DocumentUtils} from "../Utils/DocumentUtils";
import {useChangePage} from "./NavigationHooks";
import {ObjectUtils} from "../Utils/ObjectUtils";
import {useLogoutRedirect} from "./LoginHooks";

export const useCheckAuth = (targetUrl = null) => {
    const defaultTargetUrl = (window.location.pathname + window.location.search);
    useLogoutRedirect("/login?redirectUrl=" + encodeURIComponent(targetUrl ?? defaultTargetUrl));
};

export const useGetContext = () => {
    const context = useSelector(state => state.contextState.context);
    const contextClone = ObjectUtils.Clone(context);

    /**
     * Return the current context from redux with a structure like
     * {
     *     // slugs
     *     parentProgram: 'srsp',
     *     childProgram: 'instream',
     *     project: 'snake-rm-123',
     *     conservationAction: 34 // conservationActionId because they don't have slugs
     *     organization: 'ipc',
     *     // ids
     *     conservationActionContextNodeId: 123,
     *     projectContextNodeId: 123,
     *     subProgramContextNodeId: 123,
     *     programContextNodeId: 123,
     *     contextNodeId: 123,
     *     organizationId: 1
     * }
     */
    return () => {
        return contextClone;
    }
};

export const usePatchContext = () => {
    const getContext = useGetContext();
    return (newContext) => {
        return ObjectUtils.Merge(
            {...getContext()},
            {...newContext}
        )
    }
}

export const useGetUrlContextSegment = () => {
    const getContext = useGetContext();
    const getActualUrlContextSegment = _getActualUrlContextSegment();

    return (currentContext = null, allowNew = false) => {
        
        // I hate JavaScript
        if(typeof currentContext === 'string' || currentContext instanceof String) {
            return currentContext;
        }
        
        if(!currentContext) {
            currentContext = getContext();
        }

        if(!allowNew && currentContext.parentProgram === 'new') {
            currentContext.parentProgram = null;
        }
        if(!allowNew && currentContext.childProgram === 'new') {
            currentContext.childProgram = null;
        }
        if(!allowNew && currentContext.project === 'new') {
            currentContext.project = null;
        }
        if(!allowNew && currentContext.conservationAction === 'new') {
            currentContext.conservationAction = null;
        }

        return getActualUrlContextSegment(currentContext);
    };
};

export const useGetUrlToolSegment = () => {
    const getActualUrlContextSegment = _getActualUrlContextSegment();

    return () => {
        let url = window.location.pathname;
        const contextSegment = getActualUrlContextSegment();
        url = url.slice(contextSegment.length);

        if(url.startsWith("/")) {
            return url.split("/")[1];
        } else {
            return url.split("/")[0];
        }
    };
};

export const useGetQueryStringParameter = () => {
    return (key, defaultValue = null) => {
        let result = DocumentUtils.GetQueryStringParameter(key);
        return result != null ? result : defaultValue;
    };
};

export const useBuildQueryString = () => {
    return (data = null) => {
        if(data === null) {
            return "";
        } else if (false) {
            if(data.startsWith("?")) {
                return data;
            } else {
                return "?" + data;
            }
        } else {
            return "?" + ObjectUtils.BuildQueryString(data);
        }
    };
};

export const useClearContext = () => {
    const getUrlToolSegment = useGetUrlToolSegment();
    const changePage = useChangePage();

    return () => {
        let tool = getUrlToolSegment();

        changePage("/" + tool);
    };
};

export const useChangeContext = () => {
    const context = useSelector(state => state.contextState.context);
    const getUrlToolSegment = useGetUrlToolSegment();
    const getUrlContextSegment = useGetUrlContextSegment();
    const changePage = useChangePage();

    return (changedContext) => {
        let newContext = {...context, ...changedContext};
        let tool = getUrlToolSegment();

        changePage(getUrlContextSegment(newContext, true) + "/" + tool);
    };
};

export const useChangeContextFromString = () => {
    const getUrlToolSegment = useGetUrlToolSegment();
    const changePage = useChangePage();

    return (context) => {
        let tool = getUrlToolSegment();

        changePage(context + "/" + tool);
    }
};

export const useChangeTool = () => {
    const getUrlContextSegment = useGetUrlContextSegment();
    const changePage = useChangePage();

    return (tool) => {
        let currentContext = getUrlContextSegment();

        if(currentContext === "/") {
            changePage("/" + tool);
        } else {
            changePage(currentContext + "/" + tool);
        }
    }
};

export const useChangeContextAndTool = () => {
    const context = useSelector(state => state.contextState.context);
    const getUrlContextSegment = useGetUrlContextSegment();
    const changePage = useChangePage();

    return (changedContext, tool) => {
        let newContext = {...context, ...changedContext};

        changePage(getUrlContextSegment(newContext, true) + "/" + tool);
    }
};

//private method
const _getActualUrlContextSegment = () => {
    const getContext = useGetContext();

    return (currentContext = null) => {
        if(!currentContext) {
            currentContext = getContext();
        }

        let result = "";

        if(currentContext.parentProgram) {
            result += "/" + currentContext.parentProgram;
        }
        if(currentContext.childProgram) {
            result += "/" + currentContext.childProgram;
        }
        if(currentContext.project) {
            result += "/" + currentContext.project;
        }
        if(currentContext.conservationAction) {
            result += "/" + currentContext.conservationAction;
        }

        return result;
    }
};
